import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Monitor,
  Image as ImageIcon,
  Clock,
  TrendingUp,
  Star,
  Zap,
  Target,
  RefreshCw,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function RecommendationsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [screenRecs, setScreenRecs] = useState([]);
  const [contentRecs, setContentRecs] = useState([]);
  const [optimalTimes, setOptimalTimes] = useState(null);
  const [activeTab, setActiveTab] = useState('screens');

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    setLoading(true);
    try {
      const [screensRes, contentRes, timesRes] = await Promise.all([
        api.get('/recommendations/screens?limit=10'),
        api.get('/recommendations/content?limit=10'),
        api.get('/recommendations/optimal-times'),
      ]);
      setScreenRecs(screensRes.data.recommendations || []);
      setContentRecs(contentRes.data.recommendations || []);
      setOptimalTimes(timesRes.data);
    } catch (error) {
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-400';
    if (score >= 40) return 'text-amber-400';
    return 'text-zinc-400';
  };

  const getScoreBadge = (score) => {
    if (score >= 70) return { label: 'Excellent', class: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' };
    if (score >= 40) return { label: 'Good', class: 'bg-amber-500/10 text-amber-400 border-amber-500/20' };
    return { label: 'Fair', class: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recommendations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Smart Recommendations
          </h1>
          <p className="text-zinc-400 mt-1">
            AI-powered suggestions based on performance data
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadRecommendations}
          className="border-zinc-700"
          data-testid="refresh-recommendations-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{screenRecs.length}</p>
                <p className="text-sm text-zinc-400">Screen Recommendations</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Zap className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{contentRecs.length}</p>
                <p className="text-sm text-zinc-400">Content Suggestions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{optimalTimes?.peak_hours?.length || 0}</p>
                <p className="text-sm text-zinc-400">Peak Hours Identified</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="screens" className="data-[state=active]:bg-blue-600">
            <Monitor className="w-4 h-4 mr-2" />
            Screen Picks
          </TabsTrigger>
          <TabsTrigger value="content" className="data-[state=active]:bg-blue-600">
            <ImageIcon className="w-4 h-4 mr-2" />
            Content Picks
          </TabsTrigger>
          <TabsTrigger value="timing" className="data-[state=active]:bg-blue-600">
            <Clock className="w-4 h-4 mr-2" />
            Optimal Timing
          </TabsTrigger>
        </TabsList>

        {/* Screen Recommendations */}
        <TabsContent value="screens" className="mt-6">
          <div className="space-y-4">
            {screenRecs.map((rec, index) => {
              const badge = getScoreBadge(rec.score);
              return (
                <Card key={index} className="glass-card border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center">
                          <span className="text-xl font-bold text-white">#{index + 1}</span>
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{rec.screen?.name}</h3>
                          <p className="text-zinc-500 text-sm">{rec.screen?.location}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${getScoreColor(rec.score)}`}>
                            {rec.score}
                          </p>
                          <p className="text-xs text-zinc-500">Score</p>
                        </div>
                        <Badge variant="outline" className={badge.class}>
                          {badge.label}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex flex-wrap gap-4">
                      <div className="flex items-center gap-2 text-sm">
                        <TrendingUp className="w-4 h-4 text-blue-400" />
                        <span className="text-zinc-400">Recent Plays:</span>
                        <span className="text-white">{rec.metrics?.recent_plays}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        <span className="text-zinc-400">Uptime:</span>
                        <span className="text-white">{rec.metrics?.uptime}%</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Star className="w-4 h-4 text-amber-400" />
                        <span className="text-zinc-400">Total Plays:</span>
                        <span className="text-white">{rec.metrics?.total_plays?.toLocaleString()}</span>
                      </div>
                    </div>
                    
                    {rec.reason && (
                      <div className="mt-3 p-2 rounded-lg bg-blue-500/5 border border-blue-500/10">
                        <p className="text-sm text-blue-400">
                          <Zap className="w-4 h-4 inline mr-1" />
                          {rec.reason}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            
            {screenRecs.length === 0 && (
              <div className="text-center py-12">
                <Monitor className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No recommendations yet</h3>
                <p className="text-zinc-400">More data needed to generate recommendations</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Content Recommendations */}
        <TabsContent value="content" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contentRecs.map((rec, index) => (
              <Card key={index} className="glass-card border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      {rec.content?.type === 'video' ? (
                        <ImageIcon className="w-8 h-8 text-purple-400" />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-blue-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{rec.content?.name}</h3>
                      <Badge variant="outline" className="mt-1 bg-zinc-800 text-zinc-400 border-zinc-700">
                        {rec.content?.type}
                      </Badge>
                      <div className="mt-2 flex gap-4 text-sm">
                        <span className="text-zinc-400">
                          Plays: <span className="text-white">{rec.metrics?.total_plays}</span>
                        </span>
                        <span className="text-zinc-400">
                          Recent: <span className="text-white">{rec.metrics?.recent_plays}</span>
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xl font-bold ${getScoreColor(rec.score)}`}>
                        {rec.score}
                      </p>
                      <p className="text-xs text-zinc-500">Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {contentRecs.length === 0 && (
              <div className="col-span-2 text-center py-12">
                <ImageIcon className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No content recommendations</h3>
                <p className="text-zinc-400">Upload more content to see recommendations</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Optimal Timing */}
        <TabsContent value="timing" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Peak Hours */}
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white">Peak Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {optimalTimes?.peak_hours?.map((hour, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
                          <span className="text-emerald-400 font-bold text-sm">{index + 1}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{hour.hour_formatted}</p>
                          <p className="text-zinc-500 text-sm">{hour.recommendation}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                        {hour.plays} plays
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Best Times */}
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white">Recommended Time Slots</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {optimalTimes?.best_times && Object.entries(optimalTimes.best_times).map(([period, time]) => (
                    <div key={period} className="flex items-center gap-4 p-4 rounded-lg bg-zinc-800/50">
                      <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium capitalize">{period}</p>
                        <p className="text-zinc-400">{time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Hourly Distribution Chart */}
            <Card className="glass-card border-zinc-800 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white">Hourly Play Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={optimalTimes?.hourly_distribution || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis
                        dataKey="hour"
                        stroke="#71717a"
                        fontSize={12}
                        tickFormatter={(h) => `${h}:00`}
                      />
                      <YAxis stroke="#71717a" fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#18181b',
                          border: '1px solid #27272a',
                          borderRadius: '8px',
                        }}
                        formatter={(value) => [value, 'Plays']}
                        labelFormatter={(hour) => `${hour}:00 - ${hour}:59`}
                      />
                      <Bar dataKey="plays" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
